import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Page, Card, Typography } from 'seagull';
import { ArrowRight, ColorGift } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { CopyToClipboard } from '~/components/CopyToClipboard/CopyToClipboard';
import ExternalLinkButton from '~/components/ExternalLinkButton';
import { useEventReporter } from '~/event-reporter';
import { useGetUserReferral } from '~/hooks/useGetUserReferral';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useLocaleKeys } from '~/i18n';
import { sendErrReport } from '~/infra/utils';
import { useStoreActions } from '~/store/store-context';
import { helpArticles } from '~/utils/helpArticles';
import styles from './ReferralDashboard.module.scss';
import { Step } from './Step';
const textsPath = 'pages.referralDashboard';
export const GetReferralDashboardTID = (parentTestId)=>getTestIds(parentTestId);
export const ReferralDashboard = ()=>{
    const { logBI } = useStoreActions();
    const { sendEventToGtm } = useEventReporter();
    const { t } = useLocaleKeys(textsPath);
    const { referral, isLoading } = useGetUserReferral();
    const { ReferralDashboard: testIds } = useMemo(()=>GetReferralDashboardTID(), []);
    useSetDocumentTitle('Referrals');
    return /*#__PURE__*/ _jsx(Page, {
        header: t.pageTitle(),
        skin: "secondary",
        testId: testIds.Root(),
        loading: isLoading,
        children: /*#__PURE__*/ _jsxs(Card, {
            bi: {
                name: 'referral_dashboard_card'
            },
            autoHeight: true,
            padding: "m",
            className: styles.card,
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: styles.titleRow,
                    children: [
                        /*#__PURE__*/ _jsx(ColorGift, {
                            size: 48,
                            className: styles.giftIcon
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "h5",
                                    weight: "normal",
                                    children: t.cardTitle()
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    children: t.cardDescription()
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(CopyToClipboard, {
                    value: referral === null || referral === void 0 ? void 0 : referral.referralLink(),
                    testId: testIds.CopyLink(),
                    onError: ()=>sendErrReport('Failed to copy referral link'),
                    onCopy: ()=>{
                        logBI(18150);
                        sendEventToGtm('refer_a_friend_copy_link', {});
                    },
                    disabled: isLoading,
                    inlineButton: true,
                    className: styles.copyLinkField
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: styles.innerCard,
                    "data-testid": testIds.HowItWorks(),
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.howItWorksHeader,
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraph1",
                                    weight: "bold",
                                    children: t.howItWorks()
                                }),
                                /*#__PURE__*/ _jsx(ExternalLinkButton, {
                                    openInNewTab: true,
                                    href: helpArticles.referralProgram(),
                                    testId: testIds.LearnMore(),
                                    suffixIcon: /*#__PURE__*/ _jsx(ArrowRight, {
                                        size: 16
                                    }),
                                    underline: "none",
                                    bi: {
                                        name: 'referral_program_learn_more'
                                    },
                                    children: t.learnMore()
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.steps,
                            children: [
                                /*#__PURE__*/ _jsx(Step, {
                                    number: 1,
                                    text: t.stepOne()
                                }),
                                /*#__PURE__*/ _jsx(Step, {
                                    number: 2,
                                    text: t.stepTwo()
                                }),
                                /*#__PURE__*/ _jsx(Step, {
                                    number: 3,
                                    text: t.stepThree()
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    testId: testIds.CreditsEarned(),
                    children: t.creditsEarned({
                        amount: referral ? referral.creditsEarnedFormatted() : '$0'
                    })
                })
            ]
        })
    });
};
